import { supabase } from '../lib/supabase';

/**
 * 이력서 데이터를 기반으로 진행률을 계산하는 공통 함수
 * @param {Object} resumeData - 이력서 데이터
 * @returns {number} - 진행률 (0-100)
 */
export const calculateResumeProgress = (resumeData) => {
  if (!resumeData) {
    return 0;
  }
  
  const hasBasicInfo = resumeData.name || resumeData.title || resumeData.summary;
  const hasExperiences = resumeData.experiences && resumeData.experiences.length > 0;
  const hasEducation = resumeData.education && resumeData.education.length > 0;
  const hasSkills = resumeData.skills && resumeData.skills.length > 0;
  const hasKeyStrengths = (resumeData.keyStrengths || resumeData.key_strengths) && 
                         (resumeData.keyStrengths || resumeData.key_strengths).length > 0;
  
  // 실제 데이터가 없으면 0% 반환
  if (!hasBasicInfo && !hasExperiences && !hasEducation && !hasSkills && !hasKeyStrengths) {
    return 0;
  }
  
  // 연락처 정보 확인 (핸드폰번호, 이메일)
  const hasContact = (resumeData.phone && resumeData.email);
  
  // 상세한 진행률 계산
  let progressPoints = 0;
  if (hasBasicInfo) progressPoints += 10;     // 기본 정보: 10%
  if (hasExperiences) progressPoints += 30;   // 경력: 30%
  if (hasEducation) progressPoints += 15;     // 교육: 15%
  if (hasSkills) progressPoints += 15;        // 스킬: 15%
  if (hasKeyStrengths) progressPoints += 20;  // 핵심역량: 20%
  if (hasContact) progressPoints += 10;       // 연락처: 10%
  
  return Math.min(progressPoints, 100);
};

/**
 * 이력서에서 부족한 항목들을 분석하는 함수
 * @param {Object} resumeData - 이력서 데이터
 * @returns {Object} - 부족한 항목들과 메시지
 */
export const analyzeResumeGaps = (resumeData) => {
  if (!resumeData) {
    return {
      missingItems: ['모든 정보'],
      message: '이력서 데이터가 없습니다.'
    };
  }
  
  const gaps = [];
  const missingItems = [];
  
  // 각 항목별 체크
  const hasBasicInfo = resumeData.name || resumeData.title || resumeData.summary;
  const hasExperiences = resumeData.experiences && resumeData.experiences.length > 0;
  const hasEducation = resumeData.education && resumeData.education.length > 0;
  const hasSkills = resumeData.skills && resumeData.skills.length > 0;
  const hasKeyStrengths = (resumeData.keyStrengths || resumeData.key_strengths) && 
                         (resumeData.keyStrengths || resumeData.key_strengths).length > 0;
  const hasPhone = resumeData.phone && resumeData.phone.trim();
  const hasEmail = resumeData.email && resumeData.email.trim();
  
  if (!hasBasicInfo) {
    gaps.push('기본 정보 (이름, 직책, 자기소개)');
    missingItems.push('basicInfo');
  }
  if (!hasExperiences) {
    gaps.push('경력사항');
    missingItems.push('experiences');
  }
  if (!hasEducation) {
    gaps.push('학력사항');
    missingItems.push('education');
  }
  if (!hasSkills) {
    gaps.push('기술/스킬');
    missingItems.push('skills');
  }
  if (!hasKeyStrengths) {
    gaps.push('핵심역량');
    missingItems.push('keyStrengths');
  }
  if (!hasPhone) {
    gaps.push('전화번호');
    missingItems.push('phone');
  }
  if (!hasEmail) {
    gaps.push('이메일');
    missingItems.push('email');
  }
  
  if (gaps.length === 0) {
    return {
      missingItems: [],
      message: '이력서가 완성되었습니다!'
    };
  }
  
  const message = gaps.length === 1 
    ? `${gaps[0]} 항목을 추가해주세요`
    : `${gaps.slice(0, -1).join(', ')} 및 ${gaps[gaps.length - 1]} 항목을 추가해주세요`;
  
  return {
    missingItems,
    message
  };
};

/**
 * 특정 세션의 진행률을 실제 이력서 데이터 기반으로 업데이트
 * @param {string} sessionId - 세션 ID
 * @param {string} userId - 사용자 ID
 */
export const updateSessionProgressFromResumeData = async (sessionId, userId) => {
  try {
    // 이력서 데이터 조회 (연락처 정보 포함)
    const { data: resumes, error: resumeError } = await supabase
      .from('resumes')
      .select('name, title, summary, experiences, education, skills, key_strengths, phone, email')
      .eq('session_id', sessionId)
      .eq('is_active', true)
      .limit(1);

    if (resumeError) {
      return false;
    }

    const resumeData = resumes && resumes.length > 0 ? resumes[0] : null;
    const actualProgress = calculateResumeProgress(resumeData);

    // 세션의 진행률 업데이트
    const { error: updateError } = await supabase
      .from('consulting_sessions')
      .update({ progress_percentage: actualProgress })
      .eq('id', sessionId)
      .eq('user_id', userId);

    if (updateError) {
      return false;
    }

    // Session progress updated
    return true;

  } catch (error) {
    return false;
  }
};

/**
 * 모든 사용자 세션의 진행률을 실제 데이터 기반으로 동기화
 * @param {string} userId - 사용자 ID
 */
export const syncAllSessionProgress = async (userId) => {
  try {
    // 사용자의 모든 세션 조회
    const { data: sessions, error: sessionError } = await supabase
      .from('consulting_sessions')
      .select('id')
      .eq('user_id', userId);

    if (sessionError) {
      return false;
    }

    let successCount = 0;
    let totalCount = sessions?.length || 0;

    if (sessions && sessions.length > 0) {
      // 각 세션의 진행률 업데이트
      for (const session of sessions) {
        const success = await updateSessionProgressFromResumeData(session.id, userId);
        if (success) successCount++;
      }
    }

    // Progress sync completed
    return true;

  } catch (error) {
    return false;
  }
};
